/* eslint-disable unicorn/prefer-blob-reading-methods */
import angular from 'angular';
import _ from 'lodash-es';
import $ from 'jquery';
import moment from 'moment';
import APIV5 from '~/models/api';
import Form from '~/scripts/services/model/form';
import Utils from '~/scripts/services/utils';
import { ELEMENT_OF } from '../constants/common-kids';
import Repre from '~/scripts/services/model/repre';

/* globals Knitiv */

/**
 * @class FormService
 */
export default class FormService {
	#$injector;

	#$q;

	/** @type {Knitiv.RootScope} */
	#$rootScope;

	#$translate;

	/** @type {import('~/scripts/services/model/api').default} */
	#API;

	#Evaluator;

	#NodeService;

	#Notification;

	#User;

	// @ts-ignore
	#mfCache;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor(
		$injector,
	) {
		this.#$injector = $injector;
		this.#$q = this.#$injector.get('$q');
		// @ts-ignore
		this.#$rootScope = this.#$injector.get('$rootScope');
		this.#$translate = this.#$injector.get('$translate');
		this.#API = this.#$injector.get('API');
		this.#Evaluator = this.#$injector.get('Evaluator');
		this.#NodeService = this.#$injector.get('NodeService');
		this.#Notification = this.#$injector.get('Notification');
		this.#User = this.#$injector.get('User');

		/** @type {?Object<import('@knitiv/api-client-javascript').KNode, any>[]} */
		this.#mfCache = null;
	}

	/**
	 * Creates and returns a Form (Node-extended) instance
	 *
	 * @param {string} isa ISA of the form to create
	 * @returns {Form} Form instance
	 */
	create(isa) {
		const form = this.#NodeService.createForm();
		form.kid = `K_NODE;${this.#User.getRefNr()}`;
		form.isa.objid = isa;
		return form;
	}

	/**
	 * Signs the submission
	 *
	 * @param {any} submission Submission object
	 * @returns {string} Identifier string
	 * @deprecated
	 */
	// eslint-disable-next-line class-methods-use-this
	dataSignature(submission) {
		const returnValue = [];
		if (submission) {
			for (const element of Object.values(submission.data)) {
				if (Array.isArray(element)) {
					for (const value of element) {
						if (value.action !== 'DEL') {
							returnValue.push(value.kid);
						}
					}
				}
				else if (element !== undefined) {
					returnValue.push(element);
				}
			}
		}
		return returnValue.join('');
	}

	/**
	 * Delete node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID of the relevant form
	 * @returns {Promise<any>} Promise result
	 */
	delete(kid) {
		return this.#NodeService.delete(kid);
	}

	/**
	 * Saves the node from the form
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID of the relevant form
	 * @returns {Promise<any>} Promise result
	 */
	detachFile(kid) {
		return this.#NodeService.detachFile(kid);
	}

	/**
	 * Evaluate a custom script
	 *
	 * @param {string | Function} function_ Script or function to execute
	 * @param {any} arguments_ Arguments of the function
	 * @param {?string} [returnValue] Name of the variable to return
	 * @returns {*} Return result
	 */
	evaluate(function_, arguments_, returnValue = undefined) {
		let function2 = function_;
		let arguments2 = _.merge(arguments_, {
			_, $, moment, isV5: false,
		});
		let returnValue2 = returnValue;
		const component = arguments_.component ?? { key: 'unknown' };
		if (!arguments_.form && arguments_.instance) {
			arguments2.form = _.get(arguments_.instance, 'root._form', {});
		}

		const componentKey = component.key;

		if (typeof function2 === 'string') {
			if (returnValue2) {
				function2 += `;return ${returnValue2}`;
			}

			function2 = this.#Evaluator.evaluator(function2, arguments2);
			arguments2 = _.values(arguments2);
		}

		if (typeof function2 === 'function') {
			returnValue2 = this.#Evaluator.evaluate(function2, arguments2);
		}
		else if (function2) {
			console.warn(`Unknown function type for ${componentKey}`);
		}
		return returnValue2;
	}

	/**
	 * Gets the files in submission
	 *
	 * @param {Record<
	 *   import('@knitiv/api-client-javascript').KNode,
	 *   import('~/scripts/services/model/form').default
	 * >} forms
	 * @returns {import('angular').IPromise<any>}
	 */
	getFilesFromMF(forms) {
		const deferred = this.#$q.defer();

		/** @type {any[]} */
		const files = Object.values(forms)
			.map(form => form.getFilesFromMF())
			.filter(file => file !== undefined);

		if (files.length > 0) {
			// @ts-ignore
			// eslint-disable-next-line unicorn/no-reduce,promise/catch-or-return,unicorn/no-array-reduce
			files.reduce((p, file) => p.then(file.upload).then(() => {
				for (const form of Object.values(forms)) {
					const { submission } = form;
					if (file.isa.kid in submission.data) {
						for (const fd of submission.data[file.isa.kid]) {
							if (file.file.name === fd.name) {
								fd.kid = file.kid;
								fd.name = file.name;
							}
						}
					}
				}
				return file;
			}), this.#$q.when(true)).then(() => deferred.resolve(true));
		}
		else {
			deferred.resolve(true);
		}
		return deferred.promise;
	}

	/**
	 * Gets the items for saving
	 *
	 * @param {Record<import('@knitiv/api-client-javascript').KNode,import('./model/form').default>} forms
	 * @param {import('@knitiv/api-client-javascript').KNode} target
	 * @param {boolean} [noUpload] Don't upload file from fileUpload componnents
	 * @returns {Promise<import('@knitiv/api-client-javascript').Item | undefined>} Items for saving
	 */
	// eslint-disable-next-line class-methods-use-this
	// eslint-disable-next-line class-methods-use-this
	async getItemForSave(forms, target, noUpload = false) {
		/**
		 * @type {Knitiv.Item}
		 */
		const item = {
			target_objnum: target,
			submission:    {},
		};

		for (const [formKid, form] of Object.entries(forms)) {
			if (form && form.target === target) {
				item.submission[formKid] = {
					isa:    form.isa?.objid,
					params: form.submission.params || {},
				};

				// Files
				if (this.#$rootScope.kds) {
					item.submission[formKid].files = form.submission.files;
				}
				else {
					if (form.params.editableTabFile && form.submission.files?.length === 1) {
						item.submission[formKid].fileid = form.submission.files[0].fileid;
					}
				}

				if (
					(form.params.editableTabForm || form.params.editableTabForm === null)
					&& form.map
					&& (form.modified || form.formScope?.kformForm?.$dirty)
				) {

					item.submission[formKid].data = await form.exportSubmissionData({
						data: {
							...form.submission.data,
							...form.uploadFiles,
						},
					}, noUpload);

					if (Object.keys(item.submission[formKid].data).length === 0) {
						delete item.submission[formKid];
					}
				}

				if (form.params.editableTabAttribut && form.formAttribut?.$dirty) {
					item.submission[formKid].kattribut = form.attribut.map(
						(/** @type {any} */ a) => a.export(),
					);
				}

				if (form.params.editableTabRepre && form.formRepre?.$dirty) {
					item.submission[formKid].krepre = form.submission.krepre.map(
						(/** @type {import('~/scripts/services/model/repre').default} */ r) => {
							if (!(r instanceof Repre)) {
								// eslint-disable-next-line no-param-reassign
								r = new Repre(r);
							}
							return r.export();
						},
					);
				}

				if (form.params.editableTabRights && form.formRights?.$dirty) {
					item.submission[formKid].kdroit = form.droit.map(
						(/** @type {import('~/scripts/services/model/droit').default} */ d) => d.export(),
					);
					item.submission[formKid].kdroitcust = form.droitcust.map(
						(/** @type {import('~/scripts/services/model/droit-cust').default} */ d) => d.export(),
					);
					item.submission[formKid].kdroitdef = form.droitdef.map(
						(/** @type {import('~/scripts/services/model/droit-def').default} */ d) => d.export(),
					);
				}

				if (form.params.editableTabWF && form.workflow.length > 0) {
					for (const wf of form.submission.wf) {
						if (!wf.node_kid) {
							wf.node_kid = formKid;
						}
					}
					item.submission[formKid].wf = form.submission.wf.map((/** @type {any} */ wf) => ({
						...wf,
						error: undefined,
					}));
				}

				if (form.linkTo.length > 0) {
					item.submission[formKid].linkTo = form.linkTo.map((/** @type {any} */ linkTo) => ({
						path: Utils.reversePath(linkTo.path),
					}));
				}
			}
		}

		return item;
	}

	/**
	 * Creates a form of an existing node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 * @param {any} options Options to send to API
	 * @returns {Promise<import('~/scripts/services/model/form').default | false>} Promise result
	 */
	async load(isa, options) {
		let modified = false;
		if (options?.modified !== undefined) {
			// eslint-disable-next-line prefer-destructuring
			modified = options.modified;
			delete options.modified;
		}
		options.node_kid = isa;
		const responseFormGet = await APIV5.instance.formGet(options, { id: 'form-service.load' });
		if (responseFormGet?.result && responseFormGet.result.length > 0) {
			/** @type {Knitiv.FormGetResult} */
			const result = responseFormGet.result[0];

			if (!('submission' in result)) {
				result.submission = { data: {} };
			}

			return new Form(this.#$injector, {
				...result.form,
				create:     !!options.create,
				files:      result.files,
				formKid:    result.kid,
				generic:    result.generic,
				modified,
				new_isa:    result.new_isa,
				submission: result.submission,
			});
		}
		return false;
	}

	/**
	 * Loads the "business" classes
	 *
	 * @returns {Promise<any>}
	 */
	async loadClasses() {
		const classes = [
			{
				kid:  'K_NODE;SYS_CLASSE',
				name: this.#$translate.instant('form.document-type'),
				icon: 'file',
			},
		];

		const result = await APIV5.instance.objectList({
			classKid: 'K_NODE;SYS_CLASSE',
			condition: {
				link: {
					objnum2:      'K_NODE;BUSINESS_CLASS',
					class_kid:    'K_NODE;SYS_CLASSE',
					linker_kid:   '0',
					relation_kid: ELEMENT_OF,
				},
			},
		}, { id: 'form-service.load-classes' });

		let langId = null;
		const currentLang = this.#User.getContext().lang[0];
		for (const [index, values] of Object.entries(result.list)) {
			if ('K_NODE;UNIVERSEL' in values.repre_all) {
				langId = 'K_NODE;UNIVERSEL';
			}
			else if (Object.prototype.hasOwnProperty.call(values.repre_all, currentLang)) {
				langId = currentLang;
			}
			else {
				langId = Object.keys(values)[0];
			}

			if (langId in values.repre_all) {
				classes.push({
					kid:  index,
					name: values.repre_all[langId]?.name,
					icon: values.icon,
				});
			}
		}
		return classes;
	}

	/**
	 * Creates a generic form of an existing node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa
	 * @param {any} options
	 * @returns {Promise<any>}
	 */
	async loadGeneric(isa, options) {
		/** @type {any} */
		let returnValue;

		_.merge(options, { kform_kid: 0, node_kid: isa });

		const responseFormGet = await APIV5.instance.formGet(options, { id: 'form.load-generic' });
		for (const result of responseFormGet.result) {
			if (result.generic && returnValue === undefined) {
				returnValue = new Form(
					this.#$injector,
					{
						...result.form,
						submission: result.submission,
					},
				);
			}
		}

		return returnValue;
	}

	/**
	 * Lock node
	 *
	 * @param {Form} form
	 * @returns {Promise<any>}
	 */
	lock(form) {
		return this.#NodeService.lock(form);
	}

	/**
	 * Get lock informations
	 *
	 * @param {Form} form Form instance
	 * @returns {Promise<any>} Promise result
	 */
	lockInfo(form) {
		return this.#NodeService.lockInfo(form);
	}

	/**
	 * Adds new item to the MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 * @param {any} item Item created
	 * @param {import('@knitiv/api-client-javascript').KNode} formKid KID of the relevant form
	 */
	mfCacheAdd(isa, item, formKid) {
		if (!this.#mfCache) {
			this.#mfCache = {
				[isa]: [],
			};
		}
		else if (!(isa in this.#mfCache)) {
			this.#mfCache[isa] = [];
		}
		if (item.name) {
			this.#mfCache[isa].push({
				item,
				words: item.name.normalize('NFD').replaceAll(/[\u0300-\u036F]/g, '').toLocaleLowerCase().split(/\W+/),
				form:  formKid,
			});
		}
	}

	/**
	 * Clears the MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 */
	mfCacheDelete(isa) {
		if (isa in this.#mfCache) {
			this.#mfCache[isa].length = 0;
			delete this.#mfCache[isa];
		}
	}

	/**
	 * Retrieves the items from the  MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode |
	 *   import('@knitiv/api-client-javascript').KNodeMF} isa ISA of the relevant form
	 * @returns {any[]} Items from cache
	 */
	mfCacheGet(isa) {
		return this.#mfCache && isa in this.#mfCache ? this.#mfCache[isa] : [];
	}

	/**
	 * Imports an object from a JSON file
	 *
	 * @param {File} file
	 * @returns {import('angular').IPromise<any>}
	 */
	objectImportJSON(file) {
		const reader = new FileReader();
		const deferred = this.#$q.defer();

		// Closure to capture the file information.
		reader.addEventListener('load', async event => {
			const item = typeof event.target?.result === 'string' ? JSON.parse(event.target?.result) : null;
			if (item) {
				try {
					const result = await this.#API.objectImportJSON([item], { id: 'form-service.object-import-json' });
					if (result?.items.length > 0) {
						this.#Notification.info({
							title:   this.#$translate.instant('common.info'),
							message: 'Votre fichier a bien été importé',
						});
						deferred.resolve(result.items[0]);
					}
				}
				catch {
					this.#Notification.error({
						title:   this.#$translate.instant('error.title'),
						message: 'Fichier JSON incorrect !',
					});
					deferred.reject();
				}
			}
		});

		// Read in the image file as a data URL.
		reader.readAsText(file);

		return deferred.promise;
	}

	/**
	 * Reloads the form
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} nodeKid Node KID
	 * @param {any} options Options to send to API
	 * @param {Record<import('@knitiv/api-client-javascript').KNode, Form>} forms Forms instances
	 * @returns {Promise<any>} Promise result
	 */
	async refresh(nodeKid, options, forms) {
		// force modified flag in order to submit actual form to KFormSet
		Object.values(forms).map((/** @type {Form} */ form) => {
			form.modified = true;
			return form;
		});

		if (forms) {
			const items = [];
			const item = await this.getItemForSave(forms, options.target, true);
			if (item) {
				items.push(item);
				options.submission = item.submission;
			}
		}

		return this.load(nodeKid, options);
	}

	/**
	 * Resets the form (just clearing the MF Cache)
	 */
	reset() {
		this.#mfCache = {};
	}

	/**
	 * Saves the node from the form
	 *
	 * @param {Object<import('@knitiv/api-client-javascript').KNode, Form>} forms Forms collection
	 * @param {Set<import('@knitiv/api-client-javascript').KNode>} targets
	 * @returns {Promise<any>} Promise result
	 */
	async save(forms, targets) {
		const items = [];
		for (const target of targets) {
			const item = await this.getItemForSave(forms, target);
			if (item) {
				items.push(item);
			}
		}
		if (items.length > 0) {
			// item.target_objnum =
			const responseFormSet = await APIV5.instance.formSet({ items }, { id: 'form-service.save' });
			this.reset();
			if (responseFormSet?.result) {
				for (const submission of Object.values(responseFormSet.result.submission)) {
					if (submission?.data) {
						submission.data = Object.fromEntries(
							// @ts-ignore
							submission.data.map((/** @type {{ key: any; values: any; }} */ data) => [
								data.key,
								data.values
							]),
						);
					}
				}
			}
			return responseFormSet?.result;
		}

		return false;
	}

	/**
	 * Unlock node
	 *
	 * @param {Form} form Form instance
	 * @returns {*} Promise result
	 */
	unlock(form) {
		return this.#NodeService.unlock(form);
	}
}

const formService = /* @ngInject */ (
	/** @type {import('angular').auto.IInjectorService} */ $injector,
) => new FormService($injector);

angular.module('app')
	.factory('FormService', formService);
